'use client';

/**
 * Поиск в шапке.
 *
 * До сих пор это было поле из макета: оно ничего не искало и обещало ⌘K,
 * которого не было. Теперь оно ищет ровно то, что умеет API, и не обещает
 * большего.
 *
 * **Две разные ручки за одним полем.**
 *
 * 1. `GET /api/calls?search=` — по номерам подстрокой и по тексту разговора
 *    полнотекстовым индексом. Это основной путь: набранное уходит сюда.
 * 2. `GET /api/contacts/by-phone` — **точный** номер, а не поиск. Показываем
 *    отдельной строкой и только когда набранное похоже на номер: у слова
 *    «Никита» карточки клиента не найдётся, и предлагать её — врать.
 *
 * Чего здесь нет намеренно: поиска клиентов по имени. Такой ручки не
 * существует, и строка «ничего не найдено» вместо неё научила бы человека,
 * что искать людей по фамилии бесполезно, — вместо того чтобы не обещать.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/navigation';
import { Loader2, MessageSquareText, Phone, Search, UserRound, X } from 'lucide-react';
import { findContactByPhone } from '@/lib/api/endpoints';
import { useCalls } from '@/lib/api/hooks';
import { ApiError } from '@/lib/api/client';
import { useDebounced } from '@/lib/useDebounced';
import { dateTimeCompact, duration, phone as formatPhone } from '@/lib/format';

/**
 * Похоже ли набранное на телефон.
 *
 * Нормализацию делает сервер по правилам тенанта, поэтому здесь достаточно
 * грубой проверки: хватает цифр и нет букв. Короткие номера (добавочные) не
 * в счёт — контакта у «206» не бывает, это разные телефоны в разных салонах.
 */
function looksLikePhone(value: string): boolean {
  const digits = value.replace(/\D/g, '');
  return digits.length >= 9 && !/[a-zA-Zа-яА-Я]/.test(value);
}

export default function GlobalSearch() {
  const router = useRouter();
  const inputRef = useRef<HTMLInputElement>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  const [value, setValue] = useState('');
  const [open, setOpen] = useState(false);
  const [contactError, setContactError] = useState<string | null>(null);
  const [findingContact, setFindingContact] = useState(false);

  const query = useDebounced(value.trim(), 350);
  // Двух букв мало: полнотекстовый поиск по ним вернёт половину базы, а
  // запрос уйдёт на каждое нажатие.
  const enough = query.length >= 3;

  const calls = useCalls(
    { search: query, page: 1, perPage: 5, sortField: 'startedAt', sortDir: 'desc' },
    false,
    enough
  );

  /** ⌘K обещан плейсхолдером — значит должен работать. */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  /** Клик мимо закрывает: подсказка не должна перекрывать экран. */
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const goToCalls = () => {
    if (!value.trim()) return;
    setOpen(false);
    router.push(`/call-list?search=${encodeURIComponent(value.trim())}`);
  };

  const goToContact = async () => {
    const number = value.trim();
    setContactError(null);
    setFindingContact(true);
    try {
      const contact = await findContactByPhone(number);
      setOpen(false);
      router.push(`/contacts?id=${contact.id}`);
    } catch (cause) {
      // 404 здесь — обычный ответ, а не сбой: карточка заводится при первом
      // звонке, и у номера, который нам не звонил, её нет.
      setContactError(
        cause instanceof ApiError && cause.isNotFound
          ? 'Клиента с таким номером нет — карточка заводится при первом звонке'
          : cause instanceof ApiError
            ? cause.message
            : 'Не удалось найти клиента'
      );
    } finally {
      setFindingContact(false);
    }
  };

  const items = calls.data?.items ?? [];
  const total = calls.data?.total ?? 0;
  const phoneLike = looksLikePhone(value);

  return (
    <div ref={boxRef} className="relative hidden md:block">
      <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(event) => {
          setValue(event.target.value);
          setContactError(null);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(event) => event.key === 'Enter' && goToCalls()}
        placeholder="Номер или слова из разговора (⌘K)"
        className="pl-8 pr-8 py-1.5 text-sm border border-slate-200 rounded-md bg-slate-50 w-64 focus:outline-none focus:ring-2 focus:ring-brand-light focus:bg-white focus:w-80 transition-all duration-200 placeholder-slate-400"
      />
      {value && (
        <button
          onClick={() => {
            setValue('');
            setContactError(null);
            inputRef.current?.focus();
          }}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
          aria-label="Очистить"
        >
          <X size={13} />
        </button>
      )}

      {open && value.trim().length > 0 && (
        <div className="absolute right-0 mt-1 w-[28rem] bg-white border border-slate-200 rounded-lg shadow-lg overflow-hidden z-40">
          {/* Карточка клиента — только для похожего на номер: у слова
              «Никита» её не найдётся, и предлагать это значит врать. */}
          {phoneLike && (
            <button
              onClick={() => void goToContact()}
              disabled={findingContact}
              className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 border-b border-slate-100 disabled:opacity-60"
            >
              {findingContact ? (
                <Loader2 size={14} className="animate-spin text-slate-400 flex-shrink-0" />
              ) : (
                <UserRound size={14} className="text-slate-400 flex-shrink-0" />
              )}
              <span className="text-xs text-slate-700">
                Карточка клиента <span className="font-mono">{value.trim()}</span>
              </span>
            </button>
          )}

          {contactError && (
            <p className="px-3 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100 leading-relaxed">
              {contactError}
            </p>
          )}

          <button
            onClick={goToCalls}
            className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-slate-50 border-b border-slate-100"
          >
            <MessageSquareText size={14} className="text-slate-400 flex-shrink-0" />
            <span className="text-xs text-slate-700">
              Искать «{value.trim()}» в разговорах
              {enough && !calls.isPending && (
                <span className="ml-1.5 text-slate-400">— найдено {total}</span>
              )}
            </span>
            {calls.isFetching && (
              <Loader2 size={12} className="animate-spin text-slate-300 ml-auto" />
            )}
          </button>

          {!enough && (
            <p className="px-3 py-2 text-[11px] text-slate-400">
              Введите хотя бы три знака — по двум поиск вернул бы половину журнала.
            </p>
          )}

          {enough &&
            items.map((call) => (
              <button
                key={call.id}
                onClick={() => {
                  setOpen(false);
                  router.push(`/call-detail?id=${call.id}`);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-slate-50 border-b border-slate-50 last:border-0"
              >
                <Phone size={12} className="text-slate-300 flex-shrink-0" />
                <span className="text-[11px] font-mono text-slate-600 flex-shrink-0">
                  №{call.callSeq}
                </span>
                <span className="text-[11px] text-slate-700 truncate">
                  {formatPhone(
                    call.direction === 'outbound' ? call.calleeNumber : call.callerNumber
                  )}
                  {call.employeeName && (
                    <span className="text-slate-400"> · {call.employeeName}</span>
                  )}
                </span>
                <span className="ml-auto text-[10px] font-mono text-slate-400 flex-shrink-0">
                  {dateTimeCompact(call.startedAt)} · {duration(call.talkSeconds)}
                </span>
              </button>
            ))}

          {enough && !calls.isPending && items.length === 0 && (
            <p className="px-3 py-2.5 text-[11px] text-slate-400 leading-relaxed">
              Ничего не нашлось. Поиск идёт по номерам и по тексту разговора — по именам сотрудников
              и клиентов он не ищет.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
